import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { History, RefreshCw, AlertTriangle } from 'lucide-react';
import { TOOL_REGISTRY, type ToolActivity } from '@platform/shared-types';
import { InventoryPage } from '../inventory/InventoryChrome';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import { toolsApi } from '../../services/toolsApi';

/**
 * Super Admin → Tools → Tool Activity — the audit trail of every tool being
 * enabled or disabled for a tenant, newest first.
 */

const toolName = (key: string) => TOOL_REGISTRY.find((t) => t.key === key)?.name || key;

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const ToolActivityPage: React.FC = () => {
  const [rows, setRows] = useState<ToolActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await toolsApi.activity(200);
      setRows(r.activity || []);
    } catch (e: any) {
      setError(e?.message || 'Could not load tool activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => [...rows].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0)), [rows]);

  const actions = (
    <div className="flex items-center gap-5 text-sm">
      <span className="text-neutral-400">Events <b className="text-white">{rows.length}</b></span>
      <button
        type="button"
        onClick={load}
        disabled={loading}
        className="h-11 w-11 grid place-items-center rounded-lg border border-neutral-700 text-neutral-300 hover:text-white disabled:opacity-60"
        aria-label="Refresh"
        title="Refresh"
      >
        {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}
      </button>
    </div>
  );

  return (
    <InventoryPage icon={History} title="Tool Activity" crumb="Tools" actions={actions}>
      <div className="max-w-6xl space-y-4">
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={load} className="h-8 px-3 -my-1 rounded-md border border-rose-300 text-xs font-medium hover:bg-white">Retry</button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {loading && rows.length === 0 ? (
            <RubyLoader variant="section" label="Loading tool activity" />
          ) : sorted.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-stone-400">{error ? 'Nothing to show.' : 'No tool has been enabled or disabled yet.'}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-stone-50 text-left text-[11px] uppercase tracking-wider text-stone-500">
                    <th className="px-4 py-3 font-semibold">When</th>
                    <th className="px-4 py-3 font-semibold">Tool</th>
                    <th className="px-4 py-3 font-semibold">Tenant</th>
                    <th className="px-4 py-3 font-semibold">Action</th>
                    <th className="px-4 py-3 font-semibold">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {sorted.map((a) => (
                    <tr key={a.activityId} className="h-12">
                      <td className="px-4 py-2 text-stone-500 whitespace-nowrap">{fmtWhen(a.at)}</td>
                      <td className="px-4 py-2 text-stone-900 font-medium whitespace-nowrap">{toolName(a.toolKey)}</td>
                      <td className="px-4 py-2 text-stone-700">{a.tenantName || a.tenantId}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${a.action === 'enabled' ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-600'}`}>
                          {a.action === 'enabled' ? 'ENABLED' : 'DISABLED'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-stone-500">{a.actorEmail || a.actorId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </InventoryPage>
  );
};

export default ToolActivityPage;
